import * as PIXI from 'pixi.js';
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { Spine } from '../../../core/classes/spine';
import { spineKeysConfig } from '../powerOfGodsCore/constants/globalConstants';
import {
  barFXAnimationNames,
  bordersInLoaderSlotNames,
  lightLoaderSlotNames,
  lineBigStatesScale,
  lineMiddleStatesScale,
  lineSmallStatesScale,
  matrixContainerParams,
  matrixReverseLoadingImageNames,
  noWinScaleDuration,
  ringCashOutStates,
  RINGS,
  scaleOffsetForNoWinAnimation,
  shakePath,
  textSlotNames,
  textSlotsAlphaEnablingScaleValues,
} from '../powerOfGodsCore/constants/matrixConstants';

gsap.registerPlugin(MotionPathPlugin);

export class MatrixView {
  container = null;

  matrixSpine = null;

  controlStripBones = null;

  constraints = null;

  constraintDebugDots = null;

  debugDotsForTailBones = null;

  _currentScaleTweens = [];

  _currentColorTweens = [];

  _fxPulseTween = {
    big: null,
    middle: null,
    small: null,
  };

  _currentNoWinTweens = [];

  textSlots = {};

  borderSlots = {};

  reverseLoadingImageSlots = {};

  lightLoadersSlots = {};

  constructor(parentContainer, startStates) {
    this.initContainer(parentContainer);
    this.initGlobalSpine();
    this.init(startStates);
  }

  init(startStates) {
    this.initControlStripBones();
    this.initPathConstraints();
    this.initStartScale(startStates);
    this.initTextSlots();
    this.initBorderSlots();
    this.initReverseLoadingImageSlots();
    this.initBarFX();
    this.initLightLoaderSlots();
  }

  initLightLoaderSlots() {
    Object.entries(lightLoaderSlotNames).forEach(([ring, label]) => {
      const slot = this.matrixSpine.skeleton.findSlot(label);
      if (!slot) {
        return;
      }

      const idx = this.matrixSpine.skeleton.slots.indexOf(slot);
      const container = this.matrixSpine.slotContainers[idx];

      this.lightLoadersSlots[ring] = {
        label,
        container,
        isEnabled: false,
      };
    });
  }

  initBarFX() {
    this.barFXSpine = new Spine({ key: spineKeysConfig.barFX });
    this.barFXSpine.state.clearTracks();
    this.container.addChild(this.barFXSpine);
    this.barFXSpine.skeleton.updateWorldTransform();
    this.barFXSpine.y = matrixContainerParams.barY;
  }

  initReverseLoadingImageSlots() {
    Object.entries(matrixReverseLoadingImageNames).forEach(([ring, names]) => {
      this.reverseLoadingImageSlots[ring] = names
        .map((label) => {
          const slot = this.matrixSpine.skeleton.findSlot(label);
          if (!slot) {
            return null;
          }
          const idx = this.matrixSpine.skeleton.slots.indexOf(slot);
          const container = this.matrixSpine.slotContainers[idx];
          return { label, container, isEnabled: false };
        })
        .filter((entry) => entry);
    });
  }

  initTextSlots() {
    Object.entries(textSlotNames).forEach(([ring, names]) => {
      this.textSlots[ring] = names
        .map((label) => {
          const slot = this.matrixSpine.skeleton.findSlot(label);
          if (!slot) {
            return null;
          }
          const idx = this.matrixSpine.skeleton.slots.indexOf(slot);
          const container = this.matrixSpine.slotContainers[idx];
          return { label, container, isEnabled: false };
        })
        .filter((entry) => entry);
    });
  }

  initBorderSlots() {
    this.borderSlots = {};
    Object.entries(bordersInLoaderSlotNames).forEach(([ring, names]) => {
      this.borderSlots[ring] = names
        .map((label) => {
          const slot = this.matrixSpine.skeleton.findSlot(label);
          if (!slot) {
            return null;
          }
          const idx = this.matrixSpine.skeleton.slots.indexOf(slot);
          const container = this.matrixSpine.slotContainers[idx];
          // Помним состояние включён/выключен
          return { label, container, isEnabled: false };
        })
        .filter((entry) => entry);
    });
  }

  initContainer(parentContainer) {
    this.container = new PIXI.Container();
    this.container.name = 'MatrixView';
    parentContainer.addChild(this.container);
  }

  initGlobalSpine() {
    this.matrixSpine = new Spine({ key: spineKeysConfig.matrix });
    this.matrixSpine.state.clearTracks();
    this.matrixSpine.skeleton.setToSetupPose();
    this.container.addChild(this.matrixSpine);
    this.matrixSpine.skeleton.updateWorldTransform();
  }

  playShakeAnimation() {
    gsap.killTweensOf(this.container);
    gsap.to(this.container, {
      duration: 0.1,
      ease: 'power1.inOut',
      motionPath: {
        path: shakePath,
        align: true,
        autoRotate: false,
        alignOrigin: [0.5, 0.5],
      },
    });
  }

  initControlStripBones() {
    this.controlStripBones = {
      big: this.matrixSpine.skeleton.findBone('controlStrip1'),
      middle: this.matrixSpine.skeleton.findBone('controlStrip2'),
      small: this.matrixSpine.skeleton.findBone('controlStrip3'),
    };
  }

  initPathConstraints() {
    const pcs = this.matrixSpine.skeleton.pathConstraints;
    this.constraints = {
      big: pcs.find((pc) => pc.data.name === 'ring1'),
      middle: pcs.find((pc) => pc.data.name === 'ring2'),
      small: pcs.find((pc) => pc.data.name === 'ring3'),
    };
  }

  initStartScale(startStates) {
    RINGS.forEach((name) => {
      const bone = this.controlStripBones[name];
      if (bone) gsap.killTweensOf(bone);
    });
    RINGS.forEach((name) => {
      const bone = this.controlStripBones[name];
      if (!bone) return;
      const targetScale = this.getTargetScale(name, startStates[name]);
      gsap.killTweensOf(bone);
      gsap.to(bone, {
        scaleX: targetScale,
        duration: 0,
        ease: 'none',
        immediateRender: false,
        overwrite: 'auto',
        onComplete: () => {
          textSlotsAlphaEnablingScaleValues[name].forEach((value, index) => {
            if (value <= targetScale) {
              this.animateTextSlotColor(name, index, true);
              this.animateBorderSlotColor(name, index - 1, true);
            }
          });
          if (startStates[name] === ringCashOutStates[name]) {
            this.animateLastTextInLoader(name, true);
          }
          bone.scaleX = targetScale;
        },
      });
    });
  }

  updateSpine() {
    this.matrixSpine.skeleton.updateWorldTransform();
  }

  animateControlStripScaleToIndex(
    name,
    toIndex,
    { onStart, onUpdate, onComplete, duration = 0.5 }
  ) {
    if (!this.checkIsLineValid(name)) return;

    const bone = this.controlStripBones[name];
    const targetScale = this.getTargetScale(name, toIndex);
    const startScale = bone.scaleX;
    const key = name;

    const prevTween = this._currentScaleTweens[key];
    if (prevTween) {
      prevTween.kill();
      delete this._currentScaleTweens[key];
    }

    if (startScale === targetScale) {
      onComplete?.(name);
      return;
    }

    gsap.killTweensOf(bone);

    bone.scaleX = startScale;
    const tween = gsap.fromTo(
      bone,
      { scaleX: startScale },
      {
        scaleX: targetScale,
        duration,
        ease: 'power1.out',
        immediateRender: false,
        overwrite: 'auto',
        onStart: () => onStart?.(name),
        onUpdate: () => onUpdate?.(name, toIndex, bone.scaleX),
        onComplete: () => {
          delete this._currentScaleTweens[key];
          bone.scaleX = targetScale;
          onComplete?.(name);
        },
      }
    );

    this._currentScaleTweens[key] = tween;
  }

  async animateControlStripScaleForLineCashOut(
    name,
    index,
    { onStart, onUpdate, onComplete, duration = 0.5 }
  ) {
    const scales = this.getScalesArray(name);
    const lastIndex = scales.length - 1;
    const prevIndex = lastIndex > 0 ? lastIndex - 1 : lastIndex;
    const bone = this.controlStripBones[name];
    if (!this.checkIsLineValid(name)) return;
    const startScale = scales[lastIndex];
    const targetScale = scales[prevIndex];
    gsap.killTweensOf(bone);
    this._currentScaleTweens[name] = gsap.fromTo(
      bone,
      { scaleX: startScale },
      {
        scaleX: targetScale,
        duration,
        ease: 'power1.out',
        immediateRender: false,
        overwrite: 'auto',
        onStart: () => {
          onStart?.(name);
        },
        onUpdate: () => {
          onUpdate?.(name, index, bone.scaleX);
        },
        onComplete: () => {
          this._currentScaleTweens[name] = null;
          bone.scaleX = targetScale;
          onComplete?.(name);
        },
      }
    );
  }

  checkIsLineValid(name) {
    const pc = this.constraints[name];
    const bone = this.controlStripBones[name];
    return pc && bone;
  }

  animateLightLoaders(ringKey, enabling) {
    const entry = this.lightLoadersSlots[ringKey];
    if (!entry) return;
    entry.isEnabled = enabling;
    const slot = this.matrixSpine.skeleton.findSlot(entry.label);
    slot.color.a = enabling ? 1 : 0;
    this.matrixSpine.skeleton.updateWorldTransform();
  }

  animateRingsForNoWin() {
    RINGS.forEach((name) => {
      if (!this.checkIsLineValid(name)) return;

      const bone = this.controlStripBones[name];
      const originalScale = bone.scaleX;
      const targetScale = originalScale + scaleOffsetForNoWinAnimation;
      const key = `noWin-${name}`;

      // kill any in-flight “no-win” tween
      const prev = this._currentNoWinTweens[key];
      if (prev) {
        prev.kill();
        delete this._currentNoWinTweens[key];
      }

      gsap.killTweensOf(bone);

      bone.scaleX = originalScale;

      const tween = gsap.to(bone, {
        scaleX: targetScale,
        duration: noWinScaleDuration,
        ease: 'linear',
        yoyo: true,
        repeat: 1,
        onComplete: () => {
          bone.scaleX = originalScale;
          delete this._currentNoWinTweens[key];
        },
      });

      this._currentNoWinTweens[key] = tween;
    });
  }

  skipWinAccelerationAnimations() {
    const tweens = [
      ...Object.values(this._currentScaleTweens),
      ...Object.values(this._currentColorTweens || {}),
    ].filter(Boolean);

    if (tweens.length === 0) {
      return Promise.resolve();
    }

    const handles = tweens.map((tween) => {
      const orig = {
        fn: tween.vars.onComplete,
        params: tween.vars.onCompleteParams,
      };
      tween.timeScale(3);
      return { tween, orig };
    });

    return new Promise((resolve) => {
      let done = 0;
      handles.forEach(({ tween, orig }) => {
        tween.eventCallback('onComplete', () => {
          if (typeof orig.fn === 'function') {
            orig.fn.apply(tween, orig.params || []);
          }
          // eslint-disable-next-line no-plusplus
          if (++done === handles.length) {
            resolve();
          }
        });
      });
    });
  }

  animateFXBar(name) {
    this.barFXSpine.play(barFXAnimationNames[name]);
  }

  animateTextSlotColor(ringKey, index, enabling) {
    const entry = this.textSlots[ringKey]?.[index];
    if (!entry) return;
    entry.isEnabled = enabling;
    const slot = this.matrixSpine.skeleton.findSlot(entry.label);
    slot.color.r = 0;
    slot.color.g = 0;
    slot.color.b = 0;
    slot.color.a = enabling ? 0.7 : 0;
    this.matrixSpine.skeleton.updateWorldTransform();
  }

  animateReverseLoadingImages(ringKey, index, enabling) {
    const entry = this.reverseLoadingImageSlots[ringKey]?.[index];
    if (!entry) return;
    entry.isEnabled = enabling;

    const slot = this.matrixSpine.skeleton.findSlot(entry.label);
    if (!slot) return;

    const tweenKey = `${ringKey}_${index}`;

    if (this._currentColorTweens[tweenKey]) {
      this._currentColorTweens[tweenKey].kill();
      delete this._currentColorTweens[tweenKey];
    }

    if (enabling) {
      slot.color.a = 0.4;
    } else {
      const tween = gsap.to(slot.color, {
        a: 0,
        duration: 0.3,
        onComplete: () => {
          if (tween) tween.kill();
          delete this._currentColorTweens[tweenKey];
        },
      });

      this._currentColorTweens[tweenKey] = tween;
    }
  }

  animateBorderSlotColor(ringKey, index, enabling) {
    const entry = this.borderSlots[ringKey]?.[index];
    if (!entry) return;

    entry.isEnabled = enabling;

    const slot = this.matrixSpine.skeleton.findSlot(entry.label);

    slot.color.a = enabling ? 0.19 : 0;
  }

  animateLastTextInLoader(ringKey, enabling) {
    const entries = this.textSlots[ringKey];
    if (!entries?.length) return;

    const entry = entries[entries.length - 1];
    entry.isEnabled = enabling;

    const slot = this.matrixSpine.skeleton.findSlot(entry.label);
    if (!slot) return;

    const prevTween = this._fxPulseTween[ringKey];
    if (prevTween) {
      prevTween.kill();
    }

    const colorMap = {
      big: { r: 0.1, g: 0.3, b: 0.8 },
      middle: { r: 0, g: 1, b: 0 },
      small: { r: 1, g: 0, b: 0 },
    };
    const base = colorMap[ringKey] || { r: 1, g: 1, b: 1 };

    const from = {
      r: base.r,
      g: base.g,
      b: base.b,
      a: enabling ? 0 : slot.color.a,
    };

    const to = {
      r: base.r,
      g: base.g,
      b: base.b,
      a: enabling ? 0.7 : 0,
    };

    slot.color.r = from.r;
    slot.color.g = from.g;
    slot.color.b = from.b;
    slot.color.a = from.a;
    this.matrixSpine.skeleton.updateWorldTransform();

    const tween = gsap.fromTo(slot.color, from, {
      ...to,
      duration: 0.1,
      ease: 'linear',
      onUpdate: () => this.matrixSpine.skeleton.updateWorldTransform(),
      onComplete: () => {
        if (tween) tween.kill();
      },
    });

    this._fxPulseTween[ringKey] = tween;
  }

  getScalesArray(name) {
    switch (name) {
      case 'big':
        return Object.values(lineBigStatesScale);
      case 'middle':
        return Object.values(lineMiddleStatesScale);
      case 'small':
        return Object.values(lineSmallStatesScale);
      default:
        return [];
    }
  }

  getTargetScale(name, toIndex) {
    const scalesArray = this.getScalesArray(name);
    return scalesArray[toIndex];
  }

  destroy() {
    this.container.destroy({ children: true, texture: false, baseTexture: false });
    this.matrixSpine = null;
    this.controlStripBones = {};
    this.constraints = {};
    this.constraintDebugDots = {};
    this.debugDotsForTailBones = {};
  }
}
